import React, { useState, useEffect } from 'react';

interface Quest {
  id: string;
  title: string;
  description: string;
  xp: number;
  completed: boolean;
  unlocked: boolean;
}

interface AWSCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  region: string;
}

export const SESQuestReal: React.FC = () => {
  const [currentLevel, setCurrentLevel] = useState(1);
  const [xp, setXp] = useState(0);
  const [playerName, setPlayerName] = useState('');
  const [gameStarted, setGameStarted] = useState(false);
  const [credentials, setCredentials] = useState<AWSCredentials>({
    accessKeyId: '',
    secretAccessKey: '',
    region: 'us-east-1'
  });
  const [credentialType, setCredentialType] = useState<'root' | 'iam' | null>(null);
  const [savedCredentials, setSavedCredentials] = useState<any[]>([]);
  const [currentQuest, setCurrentQuest] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [quests, setQuests] = useState<Quest[]>([
    {
      id: 'iam-palace',
      title: '🏰 IAM Permission Palace',
      description: 'Set up your AWS credentials (Root or IAM)',
      xp: 100,
      completed: false,
      unlocked: true
    },
    {
      id: 'domain-dragon',
      title: '🐉 Domain Verification Dragon',
      description: 'Verify your domain with DNS records',
      xp: 200,
      completed: false,
      unlocked: false
    },
    {
      id: 'sandbox-escape',
      title: '🏖️ Sandbox Escape Room',
      description: 'Request production access from AWS',
      xp: 300,
      completed: false,
      unlocked: false
    },
    {
      id: 'weapon-choice',
      title: '⚔️ SMTP vs API Duel',
      description: 'Choose between SMTP or API integration',
      xp: 150,
      completed: false,
      unlocked: false
    },
    {
      id: 'bounce-boss',
      title: '🛡️ Bounce Handler Boss',
      description: 'Set up bounce and complaint handling',
      xp: 250,
      completed: false,
      unlocked: false
    }
  ]);

  // Load saved credentials on mount
  useEffect(() => {
    if (gameStarted) {
      fetchCredentials();
      fetchProgress();
    }
  }, [gameStarted]);

  const fetchCredentials = async () => {
    try {
      const response = await fetch('http://localhost:8787/api/aws/credentials?userId=1');
      const data = await response.json();
      if (data.success && data.credentials.length > 0) {
        setSavedCredentials(data.credentials);
      }
    } catch (err) {
      console.error('Failed to fetch credentials:', err);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await fetch('http://localhost:8787/api/aws/ses/progress?userId=1');
      const data = await response.json();
      if (data.success) {
        // Update quests based on saved progress
        setQuests(prevQuests =>
          prevQuests.map(quest => {
            const progress = data.progress.find((p: any) => p.quest_id === quest.id);
            if (progress) {
              return {
                ...quest,
                completed: progress.status === 'completed',
                unlocked: progress.status !== 'locked'
              };
            }
            return quest;
          })
        );
      }
    } catch (err) {
      console.error('Failed to fetch progress:', err);
    }
  };

  const startGame = () => {
    if (playerName.trim()) {
      setGameStarted(true);
    }
  };

  const detectCredentialType = (accessKey: string): 'root' | 'iam' => {
    // In real implementation, would check with AWS API
    // For demo, we'll use a simple heuristic
    return 'iam'; // Default to IAM for safety
  };

  const validateCredentials = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!credentials.accessKeyId || credentials.accessKeyId.length !== 20) {
      errors.push('Access Key ID must be 20 characters');
    }
    if (!credentials.secretAccessKey || credentials.secretAccessKey.length !== 40) {
      errors.push('Secret Access Key must be 40 characters');
    }
    if (!credentials.accessKeyId.match(/^[A-Z0-9]{20}$/)) {
      errors.push('Access Key ID format is invalid');
    }

    return { valid: errors.length === 0, errors };
  };

  const saveCredentials = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    const validation = validateCredentials();
    if (!validation.valid) {
      setError(validation.errors.join(', '));
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('http://localhost:8787/api/aws/credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credentials,
          userId: 1
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(data.message);
        setCredentialType(data.credentialType);

        // Update quest progress
        await updateQuestProgress('iam-palace', 'completed', 100);

        // Refresh credentials list
        await fetchCredentials();
        await fetchProgress();

        // Clear form
        setCredentials({ accessKeyId: '', secretAccessKey: '', region: 'us-east-1' });

        setTimeout(() => {
          setCurrentQuest(null);
          setSuccess(null);
        }, 3000);
      } else {
        setError(data.errors ? data.errors.join(', ') : 'Failed to save credentials');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const updateQuestProgress = async (questId: string, status: string, xpEarned: number) => {
    try {
      await fetch(`http://localhost:8787/api/aws/ses/progress/${questId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: 1,
          status,
          xpEarned
        })
      });

      setXp(prev => prev + xpEarned);

      // Check for level up
      if (xp + xpEarned >= currentLevel * 500) {
        setCurrentLevel(prev => prev + 1);
      }
    } catch (err) {
      console.error('Failed to update progress:', err);
    }
  };

  const renderQuestContent = () => {
    switch (currentQuest) {
      case 'iam-palace':
        return (
          <div className="space-y-4">
            <div className="bg-yellow-900/30 border border-yellow-600 p-3 rounded">
              <h4 className="font-bold text-yellow-400 mb-2">⚠️ AWS Credential Setup Guide</h4>
              <div className="text-sm space-y-2 text-yellow-200">
                <p><strong>For Root User:</strong></p>
                <ol className="list-decimal ml-4 space-y-1">
                  <li>Sign in to AWS Console as root user</li>
                  <li>Go to Security Credentials</li>
                  <li>Create Access Key (not recommended)</li>
                </ol>
                <p className="mt-2"><strong>For IAM User (Recommended):</strong></p>
                <ol className="list-decimal ml-4 space-y-1">
                  <li>Sign in to AWS Console</li>
                  <li>Go to IAM → Users → Add User</li>
                  <li>Attach policy: AmazonSESFullAccess</li>
                  <li>Create Access Key for programmatic access</li>
                </ol>
              </div>
            </div>

            <div className="space-y-3">
              <input
                type="text"
                placeholder="AWS Access Key ID (20 characters)"
                value={credentials.accessKeyId}
                onChange={(e) => setCredentials({...credentials, accessKeyId: e.target.value.toUpperCase()})}
                className="w-full px-3 py-2 bg-black/50 rounded border border-white/20 text-white font-mono"
                maxLength={20}
              />
              <input
                type="password"
                placeholder="AWS Secret Access Key (40 characters)"
                value={credentials.secretAccessKey}
                onChange={(e) => setCredentials({...credentials, secretAccessKey: e.target.value})}
                className="w-full px-3 py-2 bg-black/50 rounded border border-white/20 text-white font-mono"
                maxLength={40}
              />
              <select
                value={credentials.region}
                onChange={(e) => setCredentials({...credentials, region: e.target.value})}
                className="w-full px-3 py-2 bg-black/50 rounded border border-white/20 text-white"
              >
                <option value="us-east-1">US East (N. Virginia)</option>
                <option value="us-west-2">US West (Oregon)</option>
                <option value="eu-west-1">EU (Ireland)</option>
                <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
              </select>

              {error && (
                <div className="bg-red-900/30 border border-red-600 p-2 rounded text-red-300 text-sm">
                  {error}
                </div>
              )}

              {success && (
                <div className="bg-green-900/30 border border-green-600 p-2 rounded text-green-300 text-sm">
                  {success}
                </div>
              )}

              <button
                onClick={saveCredentials}
                disabled={loading || !credentials.accessKeyId || !credentials.secretAccessKey}
                className="w-full bg-blue-500 py-2 rounded font-bold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '⏳ Saving...' : '💾 Save AWS Credentials'}
              </button>
            </div>

            {savedCredentials.length > 0 && (
              <div className="mt-4 p-3 bg-white/5 rounded">
                <h5 className="font-bold mb-2">Saved Credentials:</h5>
                {savedCredentials.map((cred, idx) => (
                  <div key={idx} className="text-sm mb-1">
                    {cred.accessKeyIdMasked} ({cred.credential_type})
                    {cred.securityWarning && (
                      <span className="text-yellow-400 ml-2">⚠️</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'domain-dragon':
        return (
          <div className="space-y-4">
            <p>Domain verification coming soon...</p>
            <button
              onClick={() => updateQuestProgress('domain-dragon', 'completed', 200)}
              className="w-full bg-green-500 py-2 rounded font-bold"
            >
              Complete (Demo)
            </button>
          </div>
        );

      default:
        return (
          <div className="text-center py-8">
            <p>Quest content coming soon!</p>
          </div>
        );
    }
  };

  if (!gameStarted) {
    return (
      <div className="bg-gray-900 p-8 rounded-lg text-white border border-gray-700">
        <h1 className="text-4xl font-bold mb-4 text-center">🎮 AWS SES Quest</h1>
        <p className="text-center mb-2">The Real Deal - Set Up AWS SES Step by Step</p>
        <p className="text-center mb-6 text-sm text-yellow-400">
          ⚡ Now with real credential storage and validation!
        </p>

        <div className="max-w-md mx-auto">
          <input
            type="text"
            placeholder="Enter your hero name..."
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            className="w-full px-4 py-2 rounded bg-white/10 border border-white/20 text-white placeholder-white/50 mb-4"
            onKeyPress={(e) => e.key === 'Enter' && startGame()}
          />
          <button
            onClick={startGame}
            disabled={!playerName.trim()}
            className="w-full bg-green-600 py-3 rounded font-bold hover:bg-green-700 transition-all disabled:opacity-50"
          >
            Begin Your Quest
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 p-8 rounded-lg text-white border border-gray-700">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">🎮 {playerName}</h2>
          <p className="text-sm opacity-75">Level {currentLevel} Email Wizard</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold">{xp} XP</p>
          <div className="w-32 bg-white/20 rounded-full h-2 mt-1">
            <div
              className="bg-yellow-400 h-full rounded-full transition-all"
              style={{ width: `${(xp % 500) / 5}%` }}
            />
          </div>
        </div>
      </div>

      {/* Progress Timeline */}
      <div className="relative mb-8">
        <div className="absolute top-6 left-0 right-0 h-0.5 bg-gray-700"></div>
        <div
          className="absolute top-6 left-0 h-0.5 bg-green-500 transition-all duration-500"
          style={{ width: `${(quests.filter(q => q.completed).length / quests.length) * 100}%` }}
        ></div>
        <div className="relative flex justify-between">
          {quests.map((quest) => (
            <div
              key={quest.id}
              className={`flex flex-col items-center cursor-pointer transition-all ${
                quest.unlocked && !quest.completed ? 'hover:scale-110' : ''
              }`}
              onClick={() => quest.unlocked && !quest.completed && setCurrentQuest(quest.id)}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl border-2 transition-all ${
                quest.completed
                  ? 'bg-green-600 border-green-500 shadow-lg shadow-green-500/30'
                  : quest.unlocked
                  ? 'bg-gray-800 border-green-500 hover:border-green-400 shadow-md'
                  : 'bg-gray-800 border-gray-700'
              }`}>
                {quest.completed ? '✓' : quest.title.split(' ')[0]}
              </div>
              <span className="text-xs mt-2 max-w-[100px] text-center font-medium">
                {quest.title.split(' ').slice(1).join(' ')}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Quest List */}
      <div className="space-y-3">
        <h3 className="text-lg font-bold mb-2">📜 Quests</h3>
        {quests.map(quest => (
          <div
            key={quest.id}
            className={`p-4 rounded-lg border-2 transition-all ${
              quest.completed
                ? 'bg-green-800/30 border-green-500'
                : quest.unlocked
                ? 'bg-white/10 border-white/30 hover:bg-white/20 cursor-pointer'
                : 'bg-white/5 border-white/10 opacity-50'
            }`}
            onClick={() => quest.unlocked && !quest.completed && setCurrentQuest(quest.id)}
          >
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-bold flex items-center gap-2">
                  {quest.title}
                  {quest.completed && <span className="text-green-400">✓</span>}
                </h4>
                <p className="text-sm opacity-75">{quest.description}</p>
              </div>
              <div className="text-right">
                <p className="font-bold">{quest.xp} XP</p>
                {!quest.unlocked && <p className="text-xs">🔒 Locked</p>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quest Modal */}
      {currentQuest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 border border-gray-600 p-6 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-2xl font-bold mb-4">
              {quests.find(q => q.id === currentQuest)?.title}
            </h3>

            {renderQuestContent()}

            <div className="mt-4 flex gap-3">
              <button
                onClick={() => {
                  setCurrentQuest(null);
                  setError(null);
                  setSuccess(null);
                }}
                className="flex-1 bg-gray-600 py-2 rounded font-bold hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};